import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, of, pairwise, startWith } from 'rxjs';

import { BaseFormComponent } from '@app/forms/base-form/base-form';
import { convertEnumToOptions, Option } from '@app/forms/formly/formly-utils';
import { PortalGenderType } from '@app/services/api/api.types';
import { ApiService } from '@app/services/api/api.service';
import { errorsFormTexts } from '@app/forms/formly/validators/validators';
import { dropdownSeparatorValue } from '@voyagu/portal-design-system';
import { newTravalerOption } from '@app/utils/constants';
import type { PassengersErrors } from '@app/pages/passengers-page/passengers-page.component';
import { getTrackingEvent, PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { scrollTo } from '@app/utils/utils';

@Component({
  selector: 'passengers-form',
  templateUrl: './passengers-form.component.html',
  styleUrls: ['./passengers-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassengersFormComponent
  extends BaseFormComponent<PassengersErrors | null>
  implements AfterViewInit, OnChanges
{
  updating: boolean;

  @Input() passengersLabels: string[] = [];
  @Input() passengers: Option[] = [];

  @Output() valueChange = new EventEmitter();
  @Output() back = new EventEmitter();

  changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    private apiService: ApiService,
    private dr: DestroyRef,
  ) {
    super();
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes['passengers'] && !changes['passengers'].firstChange) {
      this.syncAvailablePassengerOptions();
    }
  }

  ngAfterViewInit() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.dr))
      .subscribe(() => this.valueChange.emit());

    this.form.statusChanges.pipe(takeUntilDestroyed(this.dr)).subscribe((value) => {
      if (value === 'INVALID') {
        if (this.form.errors?.['required']) {
          Tracking.track(PortalTrackingEvents.portalCheckoutSubmitPassengersFieldsRequiredError);
        }
      }
    });
  }

  override getFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'passengers',
        type: 'traveler-cards',
        props: {
          labels: this.passengersLabels,
        },
        fieldArray: {
          props: {
            labels: this.passengersLabels,
          },
          fieldGroup: [
            {
              key: 'id',
              type: 'dropdown',
              className: 'mb-4',
              resetOnHide: false,
              props: {
                label: 'Who is traveling?',
                placeholder: 'Select or add a traveler',
                options: new BehaviorSubject<Option[]>([]),
              },
              hooks: {
                onInit: (field: FormlyFieldConfig) => {
                  const passengerField = field.parent as FormlyFieldConfig;
                  const options$ = new BehaviorSubject<Option[]>(
                    this.getAvailablePassengerOptions(passengerField),
                  );
                  field.props = { ...field.props, options: options$ };

                  const formArray = passengerField.parent?.formControl as FormArray | undefined;

                  formArray?.valueChanges.pipe(takeUntilDestroyed(this.dr)).subscribe(() => {
                    options$.next(this.getAvailablePassengerOptions(passengerField));
                  });

                  const idControl = field.formControl;

                  if (!idControl) return;

                  idControl.valueChanges
                    .pipe(startWith(idControl.value), pairwise(), takeUntilDestroyed(this.dr))
                    .subscribe(([previous, value]) => {
                      if (this.updating) return;

                      this.updating = true;

                      // Clear a saved profile only when the customer explicitly switches to a new
                      // traveler. Re-applying "new" while the pool changes must keep typed values.
                      if (value === newTravalerOption.value) {
                        if (this.isSavedPassengerId(previous)) {
                          field.parent?.formControl?.reset({ id: newTravalerOption.value });
                        }
                      } else if (value) {
                        const foundPassenger = this.passengers?.find((p) => p.value === value);
                        const formValue = { id: value as string, ...(foundPassenger?.extra || {}) };

                        field.parent?.formControl?.patchValue(formValue);

                        // Re-apply after Formly shows fields that were hidden until assignment
                        setTimeout(() => {
                          this.updating = true;
                          field.parent?.formControl?.patchValue(formValue);
                          this.updating = false;
                        });
                      }

                      this.updating = false;
                    });
                },
              },
              expressions: {
                hide: (field) => !this.hasUnusedSavedTravelers(field.parent),
                'props.required': (field) => this.hasUnusedSavedTravelers(field.parent),
              },
              validation: {
                messages: {
                  required: errorsFormTexts.fieldRequired,
                },
              },
            },
            {
              fieldGroupClassName: 'd-flex g-5 flex-column flex-md-row mb-4',
              resetOnHide: false,
              expressions: {
                hide: (field) => this.isManualFormHidden(field.parent as FormlyFieldConfig),
              },
              fieldGroup: [
                {
                  key: 'firstName',
                  type: 'input',
                  className: 'w-100',
                  resetOnHide: false,
                  props: {
                    label: 'First name',
                    placeholder: 'First Name',
                    required: true,
                  },
                  validation: {
                    messages: {
                      required: errorsFormTexts.fieldRequired,
                    },
                  },
                  hooks: {
                    onInit: (field) =>
                      this.fieldOnInit(field, field.parent?.parent as FormlyFieldConfig),
                  },
                },
                {
                  key: 'middleName',
                  type: 'input',
                  className: 'w-100',
                  resetOnHide: false,
                  props: {
                    label: 'Middle name',
                    placeholder: '',
                  },
                  hooks: {
                    onInit: (field) =>
                      this.fieldOnInit(field, field.parent?.parent as FormlyFieldConfig),
                  },
                },
                {
                  key: 'lastName',
                  type: 'input',
                  className: 'w-100',
                  resetOnHide: false,
                  props: {
                    label: 'Last name',
                    placeholder: 'Last Name',
                    required: true,
                  },
                  validation: {
                    messages: {
                      required: errorsFormTexts.fieldRequired,
                    },
                  },
                  hooks: {
                    onInit: (field) =>
                      this.fieldOnInit(field, field.parent?.parent as FormlyFieldConfig),
                  },
                },
              ],
            },
            {
              key: 'birthday',
              type: 'date',
              className: 'mb-4',
              resetOnHide: false,
              props: {
                label: 'Date of birth, MM/DD/YYYY',
                required: true,
                disallowFuture: true,
                nextFocusSelector: '[data-id="gender"]',
              },
              expressions: {
                hide: (field) => this.isManualFormHidden(field.parent as FormlyFieldConfig),
              },
              validation: {
                messages: {
                  required: 'Please select a valid date of birth',
                  invalid: 'Please select a valid date of birth',
                },
              },
              hooks: {
                onInit: (field) => this.fieldOnInit(field, field.parent as FormlyFieldConfig),
              },
            },
            {
              fieldGroupClassName: 'd-flex g-5 flex-column flex-md-row',
              resetOnHide: false,
              expressions: {
                hide: (field) => this.isManualFormHidden(field.parent as FormlyFieldConfig),
              },
              fieldGroup: [
                {
                  key: 'gender',
                  className: 'flex-1',
                  type: 'dropdown',
                  resetOnHide: false,
                  props: {
                    label: 'Gender',
                    placeholder: 'Select a gender',
                    options: of(convertEnumToOptions(PortalGenderType)),
                    required: true,
                  },
                  validation: {
                    messages: {
                      required: errorsFormTexts.fieldRequired,
                    },
                  },
                  hooks: {
                    onInit: (field) =>
                      this.fieldOnInit(field, field.parent?.parent as FormlyFieldConfig),
                  },
                },
                {
                  key: 'passportCountryId',
                  type: 'dropdown',
                  className: 'flex-1',
                  resetOnHide: false,
                  props: {
                    label: 'Citizenship',
                    placeholder: 'Select Country',
                    required: true,
                    options: this.apiService.countries().pipe(
                      map((options) => {
                        const separatedOptions = ['US', 'GB', 'CA', 'AU'].map((code) => {
                          const index = options.findIndex(({ extra }) => extra?.code === code);
                          return options.splice(index, 1)[0];
                        });

                        return [...separatedOptions, { value: dropdownSeparatorValue }, ...options];
                      }),
                    ),
                  },
                  validation: {
                    messages: {
                      required: errorsFormTexts.fieldRequired,
                    },
                  },
                  hooks: {
                    onInit: (field) =>
                      this.fieldOnInit(field, field.parent?.parent as FormlyFieldConfig),
                  },
                },
              ],
            },
          ],
        },
      },
    ];
  }

  isManualFormHidden(passengerField: FormlyFieldConfig | undefined) {
    if (!this.hasUnusedSavedTravelers(passengerField)) return false;

    const group = passengerField?.formControl;
    const id = group?.get('id')?.value as string | undefined;
    if (id) return false;

    const value = group?.value as
      | {
          firstName?: string;
          lastName?: string;
          birthday?: string;
          gender?: string;
          passportCountryId?: string;
        }
      | undefined;

    return !(
      value?.firstName ||
      value?.lastName ||
      value?.birthday ||
      value?.gender ||
      value?.passportCountryId
    );
  }

  hasUnusedSavedTravelers(passengerField: FormlyFieldConfig | undefined) {
    return this.getAvailablePassengerOptions(passengerField).some((option) =>
      this.isSavedPassengerOption(option),
    );
  }

  syncAvailablePassengerOptions() {
    const passengerFields = this.fields[0]?.fieldGroup ?? [];

    passengerFields.forEach((passengerField) => {
      const idField = passengerField.fieldGroup?.find((field) => field.key === 'id');
      const options$ = idField?.props?.options;

      if (options$ instanceof BehaviorSubject) {
        options$.next(this.getAvailablePassengerOptions(passengerField));
      }
    });

    const options = this.fields[0]?.options as
      | { checkExpressions?: (field: FormlyFieldConfig) => void }
      | undefined;

    options?.checkExpressions?.(this.fields[0]);
  }

  getAvailablePassengerOptions(passengerField: FormlyFieldConfig | undefined) {
    const formArray = passengerField?.parent?.formControl as FormArray | undefined;
    const currentIndex = Number(passengerField?.key);
    const selectedElsewhere = new Set(
      formArray?.controls
        ?.map((control, index) => {
          if (index === currentIndex) return null;
          const id = control.get('id')?.value as string | undefined;
          if (!id || id === newTravalerOption.value || id === dropdownSeparatorValue) {
            return null;
          }
          return id;
        })
        .filter(Boolean) as string[],
    );

    return (this.passengers || []).filter((option) => {
      if (!this.isSavedPassengerOption(option)) return true;

      return !selectedElsewhere.has(option.value as string);
    });
  }

  isSavedPassengerOption(option: Option) {
    return this.isSavedPassengerId(option.value);
  }

  isSavedPassengerId(value: unknown) {
    return (
      typeof value === 'string' &&
      !!value &&
      value !== newTravalerOption.value &&
      value !== dropdownSeparatorValue
    );
  }

  fieldOnInit(field: FormlyFieldConfig, parent: FormlyFieldConfig) {
    field.formControl?.valueChanges
      .pipe(takeUntilDestroyed(this.dr))
      .subscribe((value: unknown) => {
        if (!this.updating) {
          this.updating = true;

          parent.get && parent.get('id')?.formControl?.setValue(newTravalerOption.value);

          this.updating = false;
        }

        const hasError = field.formControl?.status === 'INVALID';

        let eventName = getTrackingEvent(field.key as string);

        const index = parent.key;

        if (index) {
          eventName = eventName?.replace('{index}', index as string);
        }

        if (field.key === 'gender') {
          eventName = eventName?.replace('{value}', (value as string)?.toLowerCase() ?? '');
        }

        if (eventName) {
          Tracking.track(`${eventName}_entered`);
        }

        if (eventName && hasError) {
          Tracking.track(`${eventName}_validation_error`);
        }
      });
  }

  tryBack() {
    this.back.emit();

    Tracking.track(PortalTrackingEvents.portalCheckoutPassengersBackPressed);
  }

  override setErrors(errors: PassengersErrors) {
    if (errors.passengers?.length) {
      this.form.enable({ emitEvent: false });

      errors.passengers.forEach((error, i: number) => {
        if (error.birthday) {
          const passengersFormArray = this.form.get('passengers') as unknown as FormArray;
          passengersFormArray?.controls[i]?.get('birthday')?.setErrors({ backend: error.birthday });

          this.form.setErrors({ backend: errorsFormTexts.birthday });
          Tracking.track(
            PortalTrackingEvents.portalCheckoutPaxBirthdayError.replace('{index}', i.toString()),
          );
          this.expandFirstInvalidTraveler();
          this.scrollToFirstInvalidField();
        }
      });
    }
  }

  override submit() {
    if (this.form.disabled) return;

    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.form.disable({ emitEvent: false });
      this.changeDetectorRef.markForCheck();
      this.formSubmit.emit(this.model);
    } else {
      this.form.setErrors({ required: errorsFormTexts.required });
      this.expandFirstInvalidTraveler();
      this.changeDetectorRef.markForCheck();
      this.scrollToFirstInvalidField();
    }

    Tracking.track(PortalTrackingEvents.submitPassengers);
  }

  expandFirstInvalidTraveler() {
    const passengersFormArray = this.form.get('passengers') as unknown as FormArray;
    const firstInvalidIndex = passengersFormArray?.controls?.findIndex(
      (control) => control.invalid,
    );

    if (firstInvalidIndex == null || firstInvalidIndex < 0) return;

    const expandTraveler = this.fields[0]?.props?.['expandTraveler'] as
      | ((index: number) => void)
      | undefined;

    expandTraveler?.(firstInvalidIndex);
  }

  scrollToFirstInvalidField() {
    setTimeout(() => {
      const inputsWithError = document.querySelectorAll('.input.error');

      if (!inputsWithError?.length) return;

      const firstError = inputsWithError[0] as HTMLElement;
      scrollTo(firstError);

      const focusable =
        (firstError.matches('select, input, textarea, button')
          ? firstError
          : (firstError.querySelector('select, input, textarea, button') as HTMLElement | null)) ||
        firstError;

      focusable.focus?.();
    });
  }
}
